import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Select,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdArrowBack } from "react-icons/md";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageLayoutWithHeaderNav from "../PageLayoutWithHeaderNav";
import CoachingReportView from "../CoachingReportView";
import {
  getManagerCleanedTranscript,
  getManagerThreadReview,
} from "../../services/managerEngagement";

const TURN_RE = /^\s*([A-Za-z][A-Za-z ]*?(?:\s*\([A-Za-z]+\))?)\s*:\s*"?(.*?)\s*$/;

function parseTurns(segments) {
  const turns = [];
  for (const segment of segments || []) {
    if (segment.turns?.length) {
      turns.push(...segment.turns.map((turn) => ({
        ...turn,
        detailItemId: segment.id,
        segmentIndex: segment.index,
      })));
      continue;
    }
    let current = null;
    let turnIndex = 0;
    for (const line of String(segment.text || "").split(/\r?\n/)) {
      const match = line.match(TURN_RE);
      if (match && match[1].length <= 40) {
        if (current) turns.push(current);
        current = { speaker: match[1].trim(), text: match[2] || "", detailItemId: segment.id, turn_index: turnIndex++ };
      } else if (current && line.trim()) {
        current.text = `${current.text} ${line.trim()}`.trim();
      } else if (line.trim()) {
        current = { speaker: "Other", text: line.trim() };
      }
    }
    if (current) turns.push(current);
  }
  return turns.map((turn) => ({ ...turn, text: turn.text.replace(/^"+|"+$/g, "").trim() }));
}

function normalizedRole(speaker) {
  if (/^customer/i.test(speaker || "")) return "Customer";
  if (/^(employee|advisor|salesperson|agent)/i.test(speaker || "")) return "Employee";
  return "Other";
}

function Transcript({ segments, loading, anchor }) {
  const turns = useMemo(() => parseTurns(segments), [segments]);
  const anchorRef = useRef(null);
  useEffect(() => {
    if (!anchorRef.current) return;
    anchorRef.current.scrollIntoView?.({ behavior: "smooth", block: "center" });
    anchorRef.current.focus?.({ preventScroll: true });
  }, [turns, anchor?.detailItemId, anchor?.turnIndex]);
  if (loading) return <Flex justify="center" py={10}><Spinner /></Flex>;
  if (!turns.length) return <Text color="gray.500" py={6}>No cleaned transcript is available yet.</Text>;
  return (
    <VStack align="stretch" spacing={3}>
      {turns.map((turn, index) => {
        const role = normalizedRole(turn.speaker);
        const employee = role === "Employee";
        const cited = String(turn.detailItemId) === String(anchor?.detailItemId)
          && String(turn.turn_index) === String(anchor?.turnIndex);
        return (
          <Flex
            key={`${turn.detailItemId || "legacy"}-${turn.turn_index ?? index}-${turn.text.slice(0, 16)}`}
            ref={cited ? anchorRef : undefined}
            tabIndex={cited ? -1 : undefined}
            data-evidence-turn={cited ? "true" : undefined}
            justify={employee ? "flex-end" : "flex-start"}
            borderRadius="xl"
            outline={cited ? "3px solid" : "none"}
            outlineColor={cited ? "orange.300" : "transparent"}
            outlineOffset={cited ? "4px" : 0}
            bg={cited ? "orange.50" : "transparent"}
            px={cited ? 2 : 0}
            py={cited ? 1 : 0}
          >
            <Box maxW="82%">
              <Text fontSize="xs" color="gray.500" mb={1} fontWeight="bold" textTransform="uppercase">{role}</Text>
              <Box bg={employee ? "blue.50" : "gray.100"} borderWidth="1px" borderColor={employee ? "blue.100" : "gray.200"} borderRadius="lg" px={4} py={3}>
                <Text whiteSpace="pre-wrap" lineHeight="1.65">{turn.text}</Text>
              </Box>
            </Box>
          </Flex>
        );
      })}
    </VStack>
  );
}

function formatConversation(conversation) {
  const parsed = new Date(conversation.created_at);
  const date = Number.isNaN(parsed.getTime()) ? `Conversation ${conversation.id}` : new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
  return `${date} - ${conversation.status.replace("_", " ")}`;
}

export default function ManagerThreadReviewPage() {
  const { employeeId, threadId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [review, setReview] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [reviewCursor, setReviewCursor] = useState(null);
  const [segments, setSegments] = useState([]);
  const [transcriptCursor, setTranscriptCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [error, setError] = useState("");
  const selectedConversationId = searchParams.get("conversation") || conversations[0]?.id;
  const reportingGroupId = searchParams.get("reporting_group") || undefined;
  const locationId = searchParams.get("location") || undefined;
  const evidenceId = searchParams.get("evidence") || undefined;
  const evidenceAnchor = useMemo(() => ({
    evidenceId,
    detailItemId: searchParams.get("detail_item") || undefined,
    turnIndex: searchParams.get("turn") || undefined,
  }), [evidenceId, searchParams]);
  const scopeOptions = useMemo(
    () => reportingGroupId || locationId
      ? { reportingGroupId, locationId }
      : undefined,
    [locationId, reportingGroupId]
  );

  const loadReview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = scopeOptions
        ? await getManagerThreadReview(threadId, scopeOptions)
        : await getManagerThreadReview(threadId);
      setReview(response);
      setConversations(response.conversations || []);
      setReviewCursor(response.next_cursor || null);
      if (!searchParams.get("conversation") && response.conversations?.[0]) {
        const next = new URLSearchParams(searchParams);
        next.set("conversation", response.conversations[0].id);
        setSearchParams(next, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load this thread.");
    } finally {
      setLoading(false);
    }
  }, [scopeOptions, threadId, searchParams, setSearchParams]);

  useEffect(() => { loadReview(); }, [loadReview]);

  useEffect(() => {
    if (!selectedConversationId) return;
    let cancelled = false;
    setTranscriptLoading(true);
    setSegments([]);
    setTranscriptCursor(null);
    const transcriptOptions = {
      ...(scopeOptions || {}),
      ...(evidenceAnchor.detailItemId
        ? { anchorDetailItemId: evidenceAnchor.detailItemId }
        : {}),
    };
    const request = Object.keys(transcriptOptions).length
      ? getManagerCleanedTranscript(selectedConversationId, transcriptOptions)
      : getManagerCleanedTranscript(selectedConversationId);
    request
      .then((response) => {
        if (cancelled) return;
        setSegments(response.segments || []);
        setTranscriptCursor(response.next_cursor || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || "Could not load the cleaned transcript.");
      })
      .finally(() => { if (!cancelled) setTranscriptLoading(false); });
    return () => { cancelled = true; };
  }, [evidenceAnchor.detailItemId, scopeOptions, selectedConversationId]);

  const loadOlderConversations = async () => {
    if (!reviewCursor) return;
    const response = await getManagerThreadReview(threadId, { cursor: reviewCursor, reportingGroupId, locationId });
    setConversations((current) => [...current, ...(response.conversations || [])]);
    setReviewCursor(response.next_cursor || null);
  };

  const loadMoreTranscript = async () => {
    if (!transcriptCursor || !selectedConversationId) return;
    const response = await getManagerCleanedTranscript(selectedConversationId, { cursor: transcriptCursor, reportingGroupId, locationId });
    setSegments((current) => [...current, ...(response.segments || [])]);
    setTranscriptCursor(response.next_cursor || null);
  };

  const changeConversation = (event) => {
    const next = new URLSearchParams(searchParams);
    next.set("conversation", event.target.value);
    setSearchParams(next);
  };
  const period = searchParams.get("period");
  const jobClass = searchParams.get("job_class");
  const backParams = new URLSearchParams();
  if (period) backParams.set("period", period);
  if (jobClass) backParams.set("job_class", jobClass);
  const framework = searchParams.get("framework");
  if (framework) backParams.set("framework", framework);
  if (reportingGroupId) backParams.set("reporting_group", reportingGroupId);
  if (locationId) backParams.set("location", locationId);
  const openWeek = searchParams.get("open_week");
  const openDay = searchParams.get("open_day");
  if (openWeek) backParams.set("open_week", openWeek);
  if (openDay) backParams.set("open_day", openDay);
  const returnView = searchParams.get("return_view");
  const returnAnchor = searchParams.get("return_anchor");
  const resolvedEmployeeId = employeeId || review?.employee?.id;
  const backHash = returnAnchor ? `#${encodeURIComponent(returnAnchor)}` : "#customer-conversations";
  const backUrl = returnView === "manager_coaching"
    ? `/manager/engagement${backParams.toString() ? `?${backParams.toString()}` : ""}${returnAnchor ? `#${encodeURIComponent(returnAnchor)}` : "#coaching"}`
    : resolvedEmployeeId
      ? `/manager/employees/${resolvedEmployeeId}/engagement${backParams.toString() ? `?${backParams.toString()}` : ""}${backHash}`
      : `/manager/engagement${backParams.toString() ? `?${backParams.toString()}` : ""}`;
  return (
    <PageLayoutWithHeaderNav maxWidth="container.xl">
      <VStack align="stretch" spacing={6}>
        <Button alignSelf="flex-start" variant="ghost" size="sm" leftIcon={<MdArrowBack />} onClick={() => navigate(backUrl)}>{returnView === "manager_coaching" ? "Manager coaching" : "Advisor activity"}</Button>
        {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
        {loading ? <Flex justify="center" py={20}><Spinner /></Flex> : review ? (
          <>
            <Flex justify="space-between" gap={4} align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }}>
              <HStack spacing={4}>
                <Avatar name={review.employee.name} bg="blue.500" color="white" />
                <Box>
                  <Heading size="lg">{review.thread.title}</Heading>
                  <Text mt={1} color="gray.500">{review.employee.name}</Text>
                </Box>
              </HStack>
              <Badge colorScheme={review.manager_access_level === "coach" ? "blue" : "gray"}>{review.manager_access_level} access</Badge>
            </Flex>

            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" overflow="hidden">
              {review.report_error ? (
                <Alert status="warning"><AlertIcon />The coaching report could not be read. The transcript remains available.</Alert>
              ) : !review.report ? (
                <Box p={8}><Heading size="sm">No coaching report yet</Heading><Text mt={2} color="gray.500">The report may still be processing.</Text></Box>
              ) : (
                <CoachingReportView analysis={{ ...review.report, analysis_type: review.report_type }} threadId={threadId} displayMode="modal" />
              )}
            </Box>

            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={{ base: 4, md: 6 }}>
              <Flex justify="space-between" gap={4} align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }} mb={5}>
                <Box>
                  <Heading size="md">Cleaned transcript</Heading>
                  <Text mt={1} fontSize="sm" color="gray.500">Read-only conversation transcript</Text>
                  {evidenceId && <Text mt={1} fontSize="xs" color="orange.600" fontWeight="semibold">Supporting evidence is highlighted below.</Text>}
                </Box>
                <Select maxW={{ md: "420px" }} value={selectedConversationId || ""} onChange={changeConversation}>
                  {conversations.map((conversation) => <option key={conversation.id} value={conversation.id}>{formatConversation(conversation)}</option>)}
                </Select>
              </Flex>
              <Transcript segments={segments} loading={transcriptLoading} anchor={evidenceAnchor} />
              <HStack justify="center" mt={5}>
                {transcriptCursor && <Button size="sm" variant="outline" onClick={loadMoreTranscript}>Load more transcript</Button>}
                {reviewCursor && <Button size="sm" variant="ghost" onClick={loadOlderConversations}>Load older conversations</Button>}
              </HStack>
            </Box>
          </>
        ) : null}
      </VStack>
    </PageLayoutWithHeaderNav>
  );
}
